import { QUERAVEL_FALLBACK_MODEL, type QueravelModel } from "@rhema/client";
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { useLinks } from "../links/useLinks";

// The Queravel model the person picked. It describes this device, so it lives in localStorage.
// Nothing stored means the plan's default. Queravel needs a model on every request, so the plan's
// default is sent then: from the model list, or from this session's copy of it, or kimi-k3.

const KEY = "rhema.queravel.model";
/** The plan's default model, kept for the browser session so it is known before the list loads. */
const DEFAULT_KEY = "rhema.queravel.default_model";

export function cachedDefault(): string {
  try {
    return globalThis.sessionStorage?.getItem(DEFAULT_KEY) ?? "";
  } catch {
    return "";
  }
}

function cacheDefault(id: string) {
  try {
    globalThis.sessionStorage?.setItem(DEFAULT_KEY, id);
  } catch {
    // Storage is blocked. The list is read again next time.
  }
}
const changes = new EventTarget();

export function storedModel(): string {
  try {
    return globalThis.localStorage?.getItem(KEY) ?? "";
  } catch {
    return "";
  }
}

export function storeModel(id: string) {
  try {
    if (id) globalThis.localStorage?.setItem(KEY, id);
    else globalThis.localStorage?.removeItem(KEY);
  } catch {
    // Storage is blocked. The choice lasts until the tab closes.
  }
  memory = id;
  changes.dispatchEvent(new Event("change"));
}

let memory: string | null = null;
const current = () => memory ?? storedModel();
const subscribe = (listener: () => void) => {
  changes.addEventListener("change", listener);
  return () => changes.removeEventListener("change", listener);
};

/**
 * The model to send, always one. The person's pick while the plan still lists it, or while the
 * list cannot be read. Otherwise the plan's default from the list, then this session's copy of
 * it, then kimi-k3, the default Queravel reports.
 */
export function modelToSend(models: QueravelModel[], stored: string, cached = ""): string {
  if (stored && (!models.length || models.some((m) => m.id === stored))) return stored;
  return models.find((m) => m.default)?.id || cached || QUERAVEL_FALLBACK_MODEL;
}

/**
 * The models the person's Queravel plan allows, and the one picked on this device. When the list
 * cannot be read, there is nothing to pick and Queravel uses the plan's default.
 */
export function useQueravelModels(linked: boolean) {
  const links = useLinks();
  const [models, setModels] = useState<QueravelModel[]>([]);
  const [cached, setCached] = useState(cachedDefault);
  const stored = useSyncExternalStore(subscribe, current, current);

  useEffect(() => {
    if (!linked) return;
    let live = true;
    links.queravel
      .listModels()
      .then((list) => {
        const fallback = list.find((m) => m.default)?.id;
        if (fallback) cacheDefault(fallback);
        if (!live) return;
        setModels(list);
        if (fallback) setCached(fallback);
      })
      .catch(() => {
        if (live) setModels([]);
      });
    return () => {
      live = false;
    };
  }, [links, linked]);

  const fallback = models.find((m) => m.default) ?? models[0];
  const selected = models.some((m) => m.id === stored) ? stored : (fallback?.id ?? "");
  const choose = useCallback(
    (id: string) => storeModel(models.find((m) => m.id === id)?.default ? "" : id),
    [models],
  );
  return { models, selected, choose, send: modelToSend(models, stored, cached) };
}
